import React from 'react';
import { Card, Row, Col, Checkbox, Slider, Button } from 'antd';

const PII_TYPES = [
  { key: 'email', label: 'Email', default: 85, icon: '📧' },
  { key: 'phone', label: 'Phone', default: 80, icon: '📱' },
  { key: 'ssn', label: 'SSN', default: 95, icon: '🔒' },
  { key: 'credit_card', label: 'Credit Card', default: 85, icon: '💳' },
  { key: 'ip_address', label: 'IP Address', default: 75, icon: '🌐' },
  { key: 'name', label: 'Name', default: 65, icon: '👤' },
  { key: 'dob', label: 'Date of Birth', default: 70, icon: '📅' },
  { key: 'address', label: 'Address', default: 70, icon: '🏠' },
  { key: 'api_key', label: 'API Key', default: 85, icon: '🔑' }
];

// Helper to get threshold color based on value
const getThresholdColor = (value) => {
  if (value >= 85) return '#52c41a'; // Green - strict, safer
  if (value >= 70) return '#1890ff'; // Blue - balanced
  return '#faad14'; // Orange - lenient, more detections
};

function PIIDetectionSection({ config, updateConfig }) {
  const enabledCount = config.pii_types_enabled?.length || 9;

  return (
    <Card
      style={{ marginBottom: '24px', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}
      bodyStyle={{ padding: '20px' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#262626', letterSpacing: '0.3px' }}>
            🔐 PII DETECTION SETTINGS
          </div>
          <div style={{ fontSize: '11px', color: '#8c8c8c', marginTop: '4px' }}>
            {enabledCount} of 9 types enabled · Context-aware detection adds +20% confidence
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            size="small"
            onClick={() => {
              const globalThreshold = config.confidence_threshold || 80;
              const allTypes = {};
              PII_TYPES.forEach(type => {
                allTypes[type.key] = globalThreshold;
              });
              updateConfig('per_type_thresholds', allTypes);
            }}
          >
            📋 Apply Global
          </Button>
          <Button
            size="small"
            onClick={() => updateConfig('per_type_thresholds', null)}
          >
            ↺ Reset
          </Button>
        </div>
      </div>

      {/* PII Types - 2 Column Grid with Inline Sliders */}
      <Row gutter={[16, 16]}>
        {PII_TYPES.map(({ key, label, default: defaultVal, icon }) => {
          const isEnabled = config.pii_types_enabled?.includes(key) ?? true;
          const currentThreshold = config.per_type_thresholds?.[key] ?? defaultVal;
          const thresholdColor = getThresholdColor(currentThreshold);

          return (
            <Col span={12} key={key}>
              <div style={{
                padding: '14px',
                backgroundColor: isEnabled ? '#fafafa' : '#f5f5f5',
                borderRadius: '6px',
                border: `1px solid ${isEnabled ? thresholdColor : '#e8e8e8'}`,
                opacity: isEnabled ? 1 : 0.5,
                transition: 'all 0.3s'
              }}>
                {/* Header: Checkbox and Threshold on same line */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '10px'
                }}>
                  <Checkbox
                    checked={isEnabled}
                    onChange={(e) => {
                      const current = config.pii_types_enabled || PII_TYPES.map(t => t.value);
                      const updated = e.target.checked
                        ? [...current, key]
                        : current.filter(t => t !== key);
                      updateConfig('pii_types_enabled', updated);
                    }}
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      flex: 1
                    }}
                  >
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#262626' }}>
                      {icon} {label}
                    </span>
                  </Checkbox>
                  <span style={{
                    fontSize: '14px',
                    color: thresholdColor,
                    fontWeight: 700,
                    minWidth: '45px',
                    textAlign: 'right'
                  }}>
                    {currentThreshold}%
                  </span>
                </div>

                {/* Slider - Full Width, Fixed Height */}
                <div style={{ paddingLeft: '24px' }}>
                  <Slider
                    min={50}
                    max={100}
                    step={5}
                    value={currentThreshold}
                    onChange={(value) => {
                      const updated = { ...(config.per_type_thresholds || {}) };
                      updated[key] = value;
                      updateConfig('per_type_thresholds', updated);
                    }}
                    disabled={!isEnabled}
                    trackStyle={{
                      backgroundColor: thresholdColor,
                      height: 4,
                      transition: 'background-color 0.3s'
                    }}
                    handleStyle={{
                      borderColor: thresholdColor,
                      height: 16,
                      width: 16,
                      marginTop: -6,
                      backgroundColor: '#fff',
                      boxShadow: `0 2px 6px ${thresholdColor}60`
                    }}
                    railStyle={{
                      height: 4,
                      backgroundColor: '#e8e8e8'
                    }}
                  />
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    </Card>
  );
}

export default PIIDetectionSection;
